import { ChatViewer } from '@core/chat';
import { PresentationSlides } from '@core/presentation-panel';
import { ClientType, DisconnectReason, LiveStatus, WebinarStatus } from '@core/types';
import { ErrorMessagePage, Loader, MaintenanceNotice, MobileMenuNavigator, useLayout } from '@core/ui';
import { Chapters } from '@online-webinar/chapters';
import { joinWebinar, leaveWebinar, useAppDispatch, useAppSelector } from '@online-webinar/store';
import { useEffect, useState } from 'react';
import { onExamClose, showSurveyOrExam } from '../../../hooks';
import { VideoStreaming } from '../../molecules';
import { ChapterContent } from '../../molecules/ChapterContent';
import { ChatContent } from '../../molecules/ChatContent';
import { PresentationContent } from '../../molecules/PresentationContent';
import { TestWebinarOverlay } from '../../molecules/TestWebinarOverlay';
import { DuplicateSession } from '../../organism/DuplicateSession';
import { PollContainer } from '../../organism/PollContainer';
import { QuestionnaireOverlay } from '../../organism/QuestionnaireOverlay';
import { TestWebinarFull } from '../../organism/TestWebinarFull';
import { ViewerLiveWebinarLayout } from '../../organism/ViewerLiveWebinarLayout';
import { ConnectionOfflineOverlay } from '../ConnectionOfflineOverlay';
import { ViewEventOverlay } from '../ViewEventOverlay';

export const ViewLiveWebinarOrganism = ({
    webinarId,
    linkCode,
    anonymousId,
    anonymousAuthorizationCode,
    countdownImageUrl,
    countdownVideoUrl
}: {
    webinarId: string;
    linkCode?: string;
    anonymousId?: string;
    anonymousAuthorizationCode?: string;
    countdownImageUrl?: string;
    countdownVideoUrl?: string;
}) => {
    const { mainContainerRef, chapterActivated, chatActivated, slidesActivated, videoActivated, onMobileMenuItemClicked, isLandscape, mobileMenuEnabled } =
        useLayout();

    const dispatch = useAppDispatch();
    const [previousWebinarStatus, setPreviousWebinarStatus] = useState<WebinarStatus>(WebinarStatus.Planned);

    // Send Join Webinar
    useEffect(() => {
        dispatch(
            joinWebinar({
                webinarId: webinarId,
                linkCode: linkCode,
                anonymousId: anonymousId,
                anonymousAuthorizationCode: anonymousAuthorizationCode,
                clientType: ClientType.viewer
            })
        );
        return () => {
            dispatch(leaveWebinar());
        };
    }, [webinarId, linkCode, anonymousId, anonymousAuthorizationCode, dispatch]);

    const isInitialized = useAppSelector((state) => state.liveWebinar.isInitialized);
    const webinarStatus = useAppSelector((state) => state.liveWebinar.webinarData.status);
    const isTest = useAppSelector((state) => state.liveWebinar.webinarData.isTest);
    const dataConnection = useAppSelector((state) => state.liveWebinar.dataConnection);
    const slideDeck = useAppSelector((state) => state.liveWebinar.slideDeck);
    const activeSlideNumber = useAppSelector((state) => state.liveWebinar.activeSlideNumber);
    const chapters = useAppSelector((state) => state.liveWebinar.chapters);
    const activeChapter = useAppSelector((state) => state.liveWebinar.activeChapter);
    const survey = useAppSelector((state) => state.liveWebinar.survey);
    const surveyAnswers = useAppSelector((state) => state.liveWebinar.surveyAnswers);
    const exam = useAppSelector((state) => state.liveWebinar.exam);
    const examAnswers = useAppSelector((state) => state.liveWebinar.examAnswers);
    const unreadChatMessages = useAppSelector((state) => state.liveWebinar.unreadChatMessages);
    const videoLayout = useAppSelector((state) => state.liveWebinar.videoLayout);

    const [hasChapters, setHasChapters] = useState(false);
    const [hasSlides, setHasSlides] = useState(false);

    useEffect(() => {
        setHasChapters(chapters?.length !== 0);
    }, [chapters]);

    useEffect(() => {
        setHasSlides(slideDeck?.slides.length !== 0);
    }, [slideDeck]);

    // Show survey when switching from live to ended
    useEffect(() => {
        if (!isInitialized || !survey) {
            return;
        }
        setPreviousWebinarStatus(webinarStatus);

        if (previousWebinarStatus === WebinarStatus.Started && webinarStatus === WebinarStatus.Ended) {
            if (showSurveyOrExam(dispatch, survey, surveyAnswers, exam, examAnswers)) {
                return;
            }
        }

        if (
            previousWebinarStatus === WebinarStatus.Planned &&
            webinarStatus === WebinarStatus.Ended &&
            (!surveyAnswers || surveyAnswers.answers.length === 0)
        ) {
            if (showSurveyOrExam(dispatch, survey, surveyAnswers, exam, examAnswers)) {
                return;
            }
        }
    }, [isInitialized, webinarStatus, previousWebinarStatus, survey, surveyAnswers, exam, examAnswers, dispatch]);

    if (dataConnection.status === LiveStatus.Error) {
        const error = dataConnection.error ?? { statusCode: 500, success: false };
        return <ErrorMessagePage apiError={error} />;
    }

    // The hub refuses a full test webinar before it ever sends LiveState, so isInitialized never
    // becomes true for this viewer — this gate must precede the !isInitialized check below.
    if (dataConnection.status === LiveStatus.Offline && dataConnection.disconnectReason === DisconnectReason.TestWebinarFull) {
        return <TestWebinarFull />;
    }

    if (!isInitialized) {
        return <Loader classNames="m-auto" />;
    }

    if (dataConnection.status === LiveStatus.Offline && dataConnection.disconnectReason === DisconnectReason.DuplicateSession) {
        return <DuplicateSession />;
    }

    return (
        <ViewerLiveWebinarLayout
            ref={mainContainerRef}
            isLandscape={isLandscape}
            isMobileEnabled={mobileMenuEnabled}
            banner={<MaintenanceNotice area="live" gatewayOverlay={false} />}
        >
            <ConnectionOfflineOverlay liveStatus={dataConnection.status} />
            <QuestionnaireOverlay onClose={(type) => onExamClose(dispatch, type, survey, surveyAnswers)} />
            {webinarStatus === WebinarStatus.Planned && <ViewEventOverlay countdownImageUrl={countdownImageUrl} countdownVideoUrl={countdownVideoUrl} />}
            <VideoStreaming isActive={videoActivated} isLandscape={isLandscape} isMobile={mobileMenuEnabled} />
            {videoActivated && <TestWebinarOverlay isTest={isTest} />}
            {slidesActivated || !mobileMenuEnabled ? (
                <PresentationContent isLandscape={isLandscape} isMobile={mobileMenuEnabled} videoLayout={videoLayout}>
                    <PresentationSlides
                        slides={slideDeck?.slides ?? []}
                        currentSlideNumber={activeSlideNumber ?? 0}
                        classNames={!isLandscape ? 'object-top' : ''}
                        showFullScreen="true"
                        videoLayout={videoLayout}
                    />
                </PresentationContent>
            ) : null}
            <div
                className={`${mobileMenuEnabled ? (isLandscape ? 'row-start-1 col-start-1' : 'row-start-2 col-start-1') : 'row-span-full col-start-2'} grid grid-rows-[auto_1fr] min-h-0`}
            >
                {chapterActivated || !mobileMenuEnabled ? (
                    <ChapterContent isLandscape={isLandscape} isMobile={mobileMenuEnabled}>
                        {hasChapters && <Chapters chapters={chapters} activeChapter={activeChapter} showHeader={mobileMenuEnabled ? false : true} />}
                    </ChapterContent>
                ) : null}
                {chatActivated || !mobileMenuEnabled ? (
                    <ChatContent isMobile={mobileMenuEnabled}>
                        <ChatViewer />
                    </ChatContent>
                ) : null}
            </div>
            <PollContainer isOnDemand={false} isLandscape={isLandscape} mobileMenuEnabled={mobileMenuEnabled} />
            {mobileMenuEnabled ? (
                <MobileMenuNavigator
                    onItemClick={onMobileMenuItemClicked}
                    hasSlides={hasSlides}
                    activeSlideNumber={activeSlideNumber}
                    hasChapters={hasChapters}
                    isLandscape={isLandscape}
                    unreadChatMessages={unreadChatMessages}
                />
            ) : null}
        </ViewerLiveWebinarLayout>
    );
};
